import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../components/common/ToastProvider';
import {
    getFriendlyErrorMessage,
    signInUser,
    signUpUser,
    isAuthenticated,
} from '../lib/artTasteApi';

export default function AuthPage() {
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToast();
    const navigate = useNavigate();

    // Redirect if already authenticated
    React.useEffect(() => {
        if (isAuthenticated()) {
            navigate('/');
        }
    }, [navigate]);

    const handleInputChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            if (isLogin) {
                await signInUser(formData.email, formData.password);
                showToast('Signed in successfully!', 'success');
            } else {
                if (!formData.name.trim()) {
                    showToast('Please enter your name.', 'error');
                    return;
                }
                await signUpUser(formData.name, formData.email, formData.password);
                showToast('Account created successfully!', 'success');
            }
            navigate('/');
        } catch (error) {
            showToast(
                getFriendlyErrorMessage(error, 'Authentication failed. Please try again.'),
                'error',
            );
        } finally {
            setIsLoading(false);
        }
    };

    const toggleMode = () => {
        setIsLogin(!isLogin);
        setFormData({ name: '', email: '', password: '' });
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <Link to="/" className="brand-mark">
                            Art<span>-</span>Taste
                        </Link>
                        <h1>{isLogin ? 'Sign In' : 'Create Account'}</h1>
                        <p>
                            {isLogin
                                ? 'Welcome back! Sign in to your account.'
                                : 'Join us to start printing your documents.'}
                        </p>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        {!isLogin && (
                            <div className="field">
                                <label htmlFor="auth-name">Full Name</label>
                                <input
                                    id="auth-name"
                                    name="auth-name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleInputChange('name')}
                                    placeholder="John Doe"
                                    required={!isLogin}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        <div className="field">
                            <label htmlFor="auth-email">Email Address</label>
                            <input
                                id="auth-email"
                                name="auth-email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                placeholder="you@example.com"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="auth-password">Password</label>
                            <input
                                id="auth-password"
                                name="auth-password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange('password')}
                                placeholder="••••••••"
                                required
                                disabled={isLoading}
                                minLength="6"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn-primary full-width"
                            disabled={isLoading}
                        >
                            {isLoading
                                ? (isLogin ? 'Signing In...' : 'Creating Account...')
                                : (isLogin ? 'Sign In' : 'Create Account')}
                        </button>
                    </form>

                    <div className="auth-footer">
                        <p>
                            {isLogin ? "Don't have an account?" : 'Already have an account?'}
                            <button
                                type="button"
                                className="auth-toggle"
                                onClick={toggleMode}
                                disabled={isLoading}
                            >
                                {isLogin ? 'Sign Up' : 'Sign In'}
                            </button>
                        </p>
                        <Link to="/" className="auth-back-link">
                            ← Back to Home
                        </Link>
                    </div>
                </div>
            </div>

            <style>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem;
        }

        .auth-container {
          width: 100%;
          max-width: 400px;
        }

        .auth-card {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .auth-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .brand-mark {
          font-size: 2rem;
          font-weight: bold;
          color: #1f2937;
          text-decoration: none;
          display: inline-block;
          margin-bottom: 1rem;
        }

        .auth-header h1 {
          font-size: 1.875rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .auth-header p {
          color: #6b7280;
        }

        .auth-form {
          margin-bottom: 2rem;
        }

        .field {
          margin-bottom: 1.5rem;
        }

        .field label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .field input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .field input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .field input:disabled {
          background-color: #f9fafb;
          cursor: not-allowed;
        }

        .auth-footer {
          text-align: center;
          border-top: 1px solid #e5e7eb;
          padding-top: 1.5rem;
        }

        .auth-footer p {
          color: #6b7280;
          margin-bottom: 1rem;
        }

        .auth-toggle {
          background: none;
          border: none;
          color: #667eea;
          font-weight: 500;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 0.25rem;
        }

        .auth-toggle:hover {
          color: #5a67d8;
        }

        .auth-toggle:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .auth-back-link {
          color: #6b7280;
          text-decoration: none;
          font-size: 0.875rem;
        }

        .auth-back-link:hover {
          color: #374151;
        }
      `}</style>
        </div>
    );
}
